import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus } from "@phosphor-icons/react";
import { SEO } from "../components/site/SEO";
import { API, imgUrl, money, useCart } from "../cart";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const navigate = useNavigate();

  useEffect(() => { axios.get(`${API}/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [id]);

  if (p === false) return <main className="pt-40 px-6 text-center"><p className="font-serif text-3xl">Product not found.</p><Link to="/shop" className="font-mono text-xs underline">Back to shop</Link></main>;
  if (!p) return <main className="pt-40 px-6 text-center font-mono text-xs tracking-widest uppercase">Loading…</main>;

  const buyNow = () => { add(p, qty); navigate("/checkout"); };

  return (
    <>
      <SEO title={`${p.name} | Emporium London`} description={p.description.slice(0, 155)} path={`/product/${p.id}`} />
      <main className="relative z-10 px-6 md:px-12 lg:px-16 pt-32 md:pt-40 pb-24">
        <Link to="/shop" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)] hover:text-[var(--text-primary)]">← Back to shop</Link>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
          <div className="overflow-hidden bg-[var(--bg-tertiary)]">
            <img data-testid="product-image" src={imgUrl(p.image)} alt={p.name} className="w-full aspect-square object-cover" />
          </div>
          <div>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--gold)]">{p.category}</span>
            <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.02]">{p.name}</h1>
            <p className="mt-5 font-serif text-3xl text-[var(--text-primary)]">{money(p.price)}</p>
            <p className="mt-1 font-mono text-[10px] tracking-[0.15em] uppercase text-[var(--text-muted)]">Price includes VAT · SKU {p.sku}</p>
            <p className={`mt-4 font-mono text-[11px] tracking-[0.15em] uppercase ${p.stock > 0 ? "text-[var(--brass)]" : "text-[var(--text-muted)]"}`}>{p.stock > 0 ? `In stock (${p.stock} available)` : "Available to pre-order on request"}</p>
            <p className="mt-6 text-[var(--text-secondary)] text-lg leading-relaxed max-w-prose">{p.description}</p>

            <div className="mt-10 flex items-center gap-4">
              <div className="flex items-center border border-black/20">
                <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-12 w-12 flex items-center justify-center hover:bg-black/5"><Minus size={14} /></button>
                <span data-testid="qty-value" className="w-12 text-center font-mono text-sm">{qty}</span>
                <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="h-12 w-12 flex items-center justify-center hover:bg-black/5"><Plus size={14} /></button>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="add-to-basket" disabled={p.stock <= 0} onClick={() => { add(p, qty); toast.success("Added to basket"); }}
                className="border border-black/20 px-8 h-14 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)] transition-colors disabled:opacity-40">Add to Basket</button>
              <button data-testid="buy-now" disabled={p.stock <= 0} onClick={buyNow}
                className="bg-[var(--text-primary)] text-[var(--bg-primary)] px-8 h-14 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[var(--brass)] transition-colors disabled:opacity-40">Buy Now</button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
